/**
 * Plain-language consequences of card plays. Everything the player needs to SEE after a card lands is
 * derived here (platform agnostic): the change sentence, the struck-through narrator line and its rewrite,
 * the What changed deltas, persistent chips, callbacks in later scenes and the end-of-scene summary.
 */
import type {
  CardDef,
  ChangeRecord,
  SceneDef,
  SceneLogEntry,
  PlanResult,
  SoloRunState,
  StoryElement,
  StoryPack,
  Track,
} from '../types.js';
import data from '../../content/card-consequences.json' with { type: 'json' };

interface ConsequenceDef {
  now: string;
  did: string;
  chip: string;
  persist: boolean;
  plan?: 'blocked' | 'delayed';
  rewrite?: string;
}

const DEFS = data as Record<string, ConsequenceDef>;
const TRACKS: Track[] = ['hope', 'dread', 'chaos', 'fate'];

export function consequenceDef(card: Pick<CardDef, 'id' | 'type' | 'name'>): ConsequenceDef {
  const d = DEFS[card.id] ?? DEFS[card.id.replace(/_foil$/, '')];
  if (d) return d;
  return {
    now: `${card.name} lands on {t}.`,
    did: `played ${card.name} on {t}`,
    chip: card.name,
    persist: card.type === 'keepsake',
    plan: card.type === 'counter' ? 'blocked' : undefined,
  };
}

export function fillVars(text: string, vars: Record<string, string | number | boolean> | undefined): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

const fillT = (text: string, label: string) => text.replaceAll('{t}', label);

/** Capitalize the first letter (targets like "the bridge" start sentences). */
const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

/** Element label with live variables (Vell becomes "Frog Vell" after the frog curse). */
export function elementLabel(element: StoryElement, story: StoryPack, vars?: Record<string, string | number | boolean>): string {
  const v = vars ?? story.variables ?? {};
  if (element.id === 'villain' && typeof v.villain === 'string') return v.villain;
  if (element.id === 'hero' && typeof v.hero === 'string') return v.hero;
  return element.label;
}

export function castFor(story: StoryPack, elementId: string): { name: string; role: string } | undefined {
  return story.cast?.[elementId];
}

/** Short role for name tags, e.g. "rival heir" from "rival heir who wants to light the Crown". */
export function castShortRole(role: string): string {
  const trimmed = role.trim();
  const who = trimmed.split(/\s+who\s+/i)[0];
  const punct = who.split(/[,;]/)[0].trim();
  if (punct.length <= 48) return punct;
  const words = punct.split(/\s+/);
  return words.slice(0, 4).join(' ');
}

export function castDisplayName(elementId: string, story: StoryPack, vars?: Record<string, string | number | boolean>): string {
  const cast = castFor(story, elementId);
  if (cast?.name) return cast.name;
  const el = story.scenes.flatMap((s) => s.elements).find((e) => e.id === elementId);
  if (el) return elementLabel(el, story, vars);
  return elementId;
}

/** Whether this character has appeared in an earlier scene this run. */
export function castIsReturning(metCast: string[] | undefined, elementId: string): boolean {
  return Boolean(metCast?.includes(elementId));
}

/** Record every character in the scene as met (call when the scene ends). */
export function registerSceneCast(metCast: string[] | undefined, scene: SceneDef): string[] {
  const met = new Set(metCast ?? []);
  for (const el of scene.elements) {
    if (el.beatType === 'character') met.add(el.id);
  }
  return [...met];
}

/** Tracks that decide endings, with the ending each one leads to. */
export function endingForTrack(story: StoryPack, track: Track) {
  return story.endings
    .filter((e) => e.track === track)
    .sort((a, b) => a.minValue - b.minValue)[0];
}

export function trackLabel(story: StoryPack, track: Track): string {
  const e = endingForTrack(story, track);
  return e?.meterLabel ?? e?.title ?? track;
}

function trackDiff(a: Record<Track, number>, b: Record<Track, number>): Partial<Record<Track, number>> {
  const out: Partial<Record<Track, number>> = {};
  for (const t of TRACKS) {
    const d = (b[t] ?? 0) - (a[t] ?? 0);
    if (d) out[t] = d;
  }
  return out;
}

export interface DescribeInput {
  prev: SoloRunState;
  next: SoloRunState;
  card: CardDef;
  element: StoryElement;
  scene: SceneDef;
  story: StoryPack;
  /** the card type's risk or reward roll, if any (see rules/balance.ts) */
  swing?: { kind: string; text: string };
}

export function describePlay({ prev, next, card, element, scene, story, swing }: DescribeInput): ChangeRecord {
  const def = consequenceDef(card);
  const label = elementLabel(element, story, prev.variables);
  const counterEarlier = prev.playedThisScene.some((p) => p.cardId.startsWith('counter'));
  const plan: ChangeRecord['plan'] =
    def.plan ?? (next.variables.intent_blocked || counterEarlier ? 'blocked' : 'stands');
  return {
    sceneId: scene.id,
    sceneIndex: prev.sceneIndex,
    sceneTitle: scene.title,
    cardId: card.id,
    cardName: card.name,
    cardType: card.type,
    elementId: element.id,
    targetLabel: label,
    text: cap(fillT(def.now, label)),
    did: fillT(def.did, label),
    chip: def.chip,
    persistent: def.persist && element.beatType !== 'mood' && element.beatType !== 'event',
    plan,
    dreadDelta: next.dread - prev.dread,
    trackDeltas: trackDiff(prev.tracks, next.tracks),
    moodBefore: prev.sceneMood,
    moodAfter: next.sceneMood,
    ...(swing && swing.kind !== 'steady' ? { swing: { kind: swing.kind, text: swing.text } } : {}),
  };
}

/** Split narration into sentences, keeping quotes attached. */
function sentences(text: string): string[] {
  return text.match(/[^.!?]+[.!?]+["\u201d]?\s*/g)?.map((s) => s.trim()) ?? [text];
}

/** The narrator's own sentence about the target (falls back to the announced plan). */
export function narratorLineAbout(scene: SceneDef, element: StoryElement, story: StoryPack, vars?: Record<string, string | number | boolean>): string {
  const v = { ...(story.variables ?? {}), ...(vars ?? {}) };
  const text = fillVars(scene.narratorText, v);
  const label = elementLabel(element, story, v);
  const needles = [label, element.label, label.split(' ').pop() ?? label].filter((n) => n.length > 2);
  for (const s of sentences(text)) {
    const low = s.toLowerCase();
    if (needles.some((n) => low.includes(n.toLowerCase()))) return s;
  }
  return AS_WRITTEN[element.beatType].replace('{t}', label);
}

/** What the outline said about a target the narration never names outright. */
const AS_WRITTEN: Record<StoryElement['beatType'], string> = {
  character: '{t} does exactly what the outline says.',
  place: '{t} stays exactly as the outline describes it.',
  object: '{t} sits right where the outline put it.',
  event: '{t} happens on schedule, as outlined.',
  mood: '{t} hangs over the scene, exactly as planned.',
};

export interface Rewrite {
  /** the line that gets struck through */
  before: string;
  /** what replaces it */
  after: string;
  /** true when the struck line is the narrator's announced plan */
  isPlan: boolean;
}

export function rewriteFor(change: ChangeRecord, scene: SceneDef, element: StoryElement, story: StoryPack, vars?: Record<string, string | number | boolean>): Rewrite {
  const v = { ...(story.variables ?? {}), ...(vars ?? {}) };
  const def = DEFS[change.cardId] ?? DEFS[change.cardId.replace(/_foil$/, '')];
  if (change.plan !== 'stands' && def?.rewrite) {
    return { before: fillVars(scene.intent, v), after: def.rewrite, isPlan: true };
  }
  return { before: narratorLineAbout(scene, element, story, vars), after: change.text, isPlan: false };
}

export interface ChangeLine {
  kind: 'change' | 'dread' | 'track' | 'plan' | 'mood' | 'swing';
  text: string;
  value?: number;
}

/**
 * Plain What changed lines, e.g. "Dread +1" "Written in Wax ending +2".
 * The change sentence itself is included only when asked, since the rewrite usually shows it already.
 * playsThisScene lets the plan line say what the plan will cost at scene end.
 */
export function whatChanged(change: ChangeRecord, story: StoryPack, opts: { includeChange?: boolean; playsThisScene?: number } = {}): ChangeLine[] {
  const lines: ChangeLine[] = opts.includeChange ? [{ kind: 'change', text: change.text }] : [];
  const n = opts.playsThisScene ?? 1;
  if (change.plan === 'blocked') lines.push({ kind: 'plan', text: "Narrator's plan blocked, so it adds no Dread this scene" });
  else if (change.plan === 'delayed') lines.push({ kind: 'plan', text: "Narrator's plan delayed, so it adds no Dread this scene" });
  else if (n >= 2) lines.push({ kind: 'plan', text: "Narrator's plan bent off course, so it adds no Dread this scene" });
  else lines.push({ kind: 'plan', text: `Narrator's plan still on track: Dread +${PLAN_DREAD.stands} at scene end unless you play another card or a Counter` });
  if (change.swing) lines.push({ kind: 'swing', text: change.swing.text, value: change.swing.kind === 'backfire' ? -1 : 1 });
  if (change.dreadDelta) lines.push({ kind: 'dread', text: `Dread ${change.dreadDelta > 0 ? '+' : ''}${change.dreadDelta}`, value: change.dreadDelta });
  for (const t of ['hope', 'chaos', 'fate'] as Track[]) {
    const d = change.trackDeltas[t];
    if (d) lines.push({ kind: 'track', text: `${trackLabel(story, t)} ending ${d > 0 ? '+' : ''}${d}`, value: d });
  }
  if (change.moodAfter !== change.moodBefore) lines.push({ kind: 'mood', text: `Scene turns ${change.moodAfter}` });
  return lines;
}

export interface SceneChip {
  elementId: string;
  label: string;
  chip: string;
  sceneTitle: string;
  fromEarlier: boolean;
}

/** Chips to pin on the scene: everything played here, plus lasting changes to anyone present. */
export function sceneChips(history: ChangeRecord[] | undefined, scene: SceneDef, sceneIndex: number): SceneChip[] {
  const ids = new Set(scene.elements.map((e) => e.id));
  const out: SceneChip[] = [];
  const seen = new Set<string>();
  for (const c of [...(history ?? [])].reverse()) {
    const here = c.sceneIndex === sceneIndex;
    if (!here && !(c.persistent && ids.has(c.elementId))) continue;
    const key = `${c.elementId}:${c.chip}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ elementId: c.elementId, label: c.targetLabel, chip: c.chip, sceneTitle: c.sceneTitle, fromEarlier: !here });
  }
  return out.slice(0, 6);
}

const COPES = [
  'the narrator has quietly rewritten this scene around it.',
  'the outline now carries a hastily added footnote.',
  'everyone here is politely pretending it was always that way.',
  'the narrator is still insisting that was the plan.',
];

/** "Because you turned Archivist Vell into a frog, ..." for the start of a later scene. */
export function callbackLine(history: ChangeRecord[] | undefined, scene: SceneDef, sceneIndex: number): string | null {
  if (!history?.length) return null;
  const ids = new Set(scene.elements.map((e) => e.id));
  const earlier = history.filter((c) => c.sceneIndex < sceneIndex);
  const hit = [...earlier].reverse().find((c) => c.persistent && ids.has(c.elementId)) ??
    [...earlier].reverse().find((c) => c.sceneIndex === sceneIndex - 1 && c.persistent);
  if (!hit) return null;
  return `Because you ${hit.did}, ${COPES[sceneIndex % COPES.length]}`;
}

/** Build the Story so far entry for a finished scene. */
/** Dread the narrator's plan adds at scene end, by how hard you pushed back. */
export const PLAN_DREAD: Record<PlanResult, number> = { blocked: 0, delayed: 0, bent: 0, stands: 1, untouched: 2 };

/** Resolve the narrator's plan for the current scene from the cards played in it. */
export function planOutcome(state: SoloRunState): { plan: PlanResult; dread: number } {
  const plays = (state.history ?? []).filter((c) => c.sceneIndex === state.sceneIndex);
  const n = Math.max(plays.length, state.playedThisScene.length);
  const plan: PlanResult = n === 0
    ? 'untouched'
    : plays.some((p) => p.plan === 'blocked') || state.variables.intent_blocked
      ? 'blocked'
      : plays.some((p) => p.plan === 'delayed')
        ? 'delayed'
        : n >= 2
          ? 'bent'
          : 'stands';
  return { plan, dread: PLAN_DREAD[plan] };
}

export function sceneLogEntry(state: SoloRunState, scene: SceneDef): SceneLogEntry {
  const plays = (state.history ?? []).filter((c) => c.sceneIndex === state.sceneIndex);
  const start = state.sceneStart ?? { dread: state.dread, tracks: state.tracks };
  const { plan, dread: planDread } = planOutcome(state);
  return {
    sceneIndex: state.sceneIndex,
    sceneId: scene.id,
    title: scene.title,
    summary: scene.summary ?? scene.title,
    plays,
    plan,
    planDread,
    dreadDelta: state.dread - start.dread,
    trackDeltas: trackDiff(start.tracks, state.tracks),
  };
}

/** Position of a scene inside its chapter: "Chapter 2, Scene 3 of 9". */
export function scenePosition(story: StoryPack, sceneIndex: number): { chapterNumber: number; chapterTitle: string; sceneInChapter: number; scenesInChapter: number } {
  const scene = story.scenes[sceneIndex];
  const ch = story.chapters?.find((c) => c.id === scene?.chapterId) ?? story.chapters?.[0];
  if (!ch) return { chapterNumber: 1, chapterTitle: story.title, sceneInChapter: sceneIndex + 1, scenesInChapter: story.scenes.length };
  const ids = story.scenes.map((s, i) => (s.chapterId === ch.id ? i : -1)).filter((i) => i >= 0);
  return { chapterNumber: ch.number, chapterTitle: ch.title, sceneInChapter: ids.indexOf(sceneIndex) + 1, scenesInChapter: ids.length };
}

const SCENE_END: Record<PlanResult, { expression: string; lines: string[] }> = {
  untouched: {
    expression: 'smug',
    lines: [
      'Exactly as written. Not one interruption. Vell will be delighted.',
      'See? This is how a scene is supposed to go. Page turned, plan delivered.',
      'Lovely. Quiet. Obedient. One more of those and the Crown is as good as lit.',
    ],
  },
  stands: {
    expression: 'annoyed',
    lines: [
      'One edit. I have already pencilled around it. The plan went ahead.',
      'You {did}. Noted. The plan still happened, and the outline still holds.',
      'A smudge, not a rewrite. Vell\u2019s outline survives another page.',
    ],
  },
  bent: {
    expression: 'flustered',
    lines: [
      'That was not the scene I wrote. I am choosing to call it a first draft.',
      'You {did}, and then you kept going. My plan is bent into a shape I do not recognise.',
      'Fine. FINE. The plan happened sideways, which is a direction I do not have notes for.',
    ],
  },
  delayed: {
    expression: 'exasperated',
    lines: [
      'Late, but it happened. Mostly. In a sense. Let us move on before anyone checks.',
      'My plan arrived a beat behind, like a guest who forgot the invitation.',
    ],
  },
  blocked: {
    expression: 'defeated',
    lines: [
      'You stopped it. You actually stopped it. I need a moment, and so does Vell.',
      'My plan is crossed out. In my own book. In ink.',
      'That was the plan. Was. Past tense. I hate past tense.',
    ],
  },
};

/** The narrator's reaction once a scene wraps, so the panel never sits empty during the summary. */
export function narratorSceneEnd(entry: SceneLogEntry): { expression: string; line: string } {
  const set = SCENE_END[entry.plan];
  const last = entry.plays[entry.plays.length - 1];
  const pool = last ? set.lines : set.lines.filter((l) => !l.includes('{did}'));
  const pick = pool[(entry.sceneIndex * 7 + entry.plays.length) % pool.length];
  return { expression: set.expression, line: last ? pick.replace('{did}', last.did) : pick };
}
